import { PhoneField as FlosPhoneField, formatPhone } from '@flos/react-ui';
import { PhoneField as PhoneFieldType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import useAppState from '../../state/appState';
import { useEffect, useState } from 'react';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValue, ElementValueHuman } from '../../types/element-values';
import { getValue } from '../values/Get';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const PhoneField = ({ caption, placeholder, elementId }: PhoneFieldType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);
    const valueIsDefinedInState = formValues[elementId] === undefined ? false : true;

    const value = PhoneField.getValueOrDefault(formValues, elementId);

    useEffect(() => {
        if (!valueIsDefinedInState) {
            setFormValue(elementId, value);
        }
    }, [valueIsDefinedInState, setFormValue, elementId, value]);

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    const setValue = (value: string) => {
        setFormValue(elementId, value);
    };

    const isRequiredField = isRequired(formConfig, elementId);
    const isValid = PhoneField.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <FlosPhoneField
                value={value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setValue(value);
                }}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                errorText={errorMessage}
                isValid={isValid}
                id={elementId}
                key={elementId}
                label={placeholder}
                required={isRequiredField}
                aria-label={caption}
                data-testid="phone-field"
            />
        </>
    );
};

PhoneField.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as PhoneFieldType;
    const { summaryCaption } = element;
    const value = getValue(formConfig, formValues, elementId);
    const formattedValue = formatPhone(value) as string;

    if (!formattedValue) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={formattedValue} />;
};

PhoneField.getValueSubmit = (formValues: FormValues, elementId: string, caption: string): ClaimFormSubmitFieldValue => {
    const phone = PhoneField.getValue(formValues, elementId);

    const valueIdPhone = { string: phone };

    return getClaimFormSubmitValue('phoneField', elementId, caption, phone, valueIdPhone);
};

PhoneField.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    const value = formValues[elementId];
    return value;
};

PhoneField.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = PhoneField.getValue(formValues, elementId);

    if (value === undefined) {
        return '';
    }
    return value;
};

PhoneField.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

PhoneField.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }
    const isValid = PhoneField.isValid(formConfig, formValues, elementId);
    return isValid;
};

PhoneField.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = PhoneField.getValue(formValues, elementId);
    return value;
};

export default PhoneField;
